import * as React from 'react';
import { AddListener } from './modalDropdownTypes';
import { debounce } from 'lodash';
import { defaultPosition } from '../dropdownConsts';
import { getPositionAndMaxHeightHorizontally } from '../dropdownHelpers';
import { IconName } from '@ardoq/icons';
import { ModalBox } from './ModalBox';
import { POPOVER_WRAPPER_CLASSNAME } from '@ardoq/popovers';
import { DropdownMenuOption, DropdownSize } from '@ardoq/dropdown-menu-ui';
import { DropdownItem, DropdownSubmenu, Position } from './types';

export type RenderSubmenuList = (props: {
  options: DropdownItem[];
  addKeyboardListener: AddListener;
  removeKeyboardListener: AddListener;
  closeMenu: VoidFunction;
}) => React.ReactNode;

type ModalSubmenuProps = {
  addKeyboardListener: AddListener;
  removeKeyboardListener: AddListener;
  renderSubmenuList: RenderSubmenuList;
  focused?: boolean;
  isScrollable?: boolean;
  minDropdownSize?: DropdownSize;
  maxDropdownSize?: DropdownSize;
  testId?: string;
  intercomTarget?: string;
} & DropdownSubmenu;

interface ModelSubmenuState {
  isOpen: boolean;
  position: Position | null;
}

class ModalSubmenu extends React.Component<
  ModalSubmenuProps,
  ModelSubmenuState
> {
  state = {
    isOpen: false,
    position: null,
  };

  ref: React.MutableRefObject<HTMLDivElement | null> = React.createRef();

  constructor(props: ModalSubmenuProps) {
    super(props);
  }

  onMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    this.openSubmenu(event);
  };

  onClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target === this.ref.current) {
      this.openSubmenu(event);
    }
  };

  onMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    this.closeMenuDebounced();
  };

  onMouseOver = (event: MouseEvent) => {
    const current = this.ref.current;
    const target = event.target as HTMLElement;
    const isOverPopover = target.closest(`.${POPOVER_WRAPPER_CLASSNAME}`);
    if (current && !(current.contains(target) || isOverPopover)) {
      this.closeMenuDebounced();
    }
  };

  closeMenu = () => this.setState({ isOpen: false });

  // eslint-disable-next-line @typescript-eslint/member-ordering
  closeMenuDebounced = debounce(this.closeMenu, 10);

  getHostBox = () => {
    if (this.ref.current) {
      return this.ref.current.getBoundingClientRect();
    }
    return defaultPosition;
  };

  componentDidMount() {
    document.addEventListener('mouseover', this.onMouseOver);
  }

  componentWillUnmount() {
    document.removeEventListener('mouseover', this.onMouseOver);
  }

  render() {
    const {
      name,
      label,
      options,
      focused,
      iconName,
      addKeyboardListener,
      removeKeyboardListener,
      renderSubmenuList,
      isActive,
      isDisabled,
      popoverProps,
      tooltipText,
      isScrollable = true,
      minDropdownSize,
      maxDropdownSize,
      testId,
      intercomTarget,
      description,
      leftContent,
      leftIcon,
    } = this.props;
    return (
      <div ref={this.ref} onMouseEnter={this.onMouseEnter}>
        <DropdownMenuOption
          key={name}
          isChecked={isActive}
          isDisabled={isDisabled}
          leftIconName={iconName}
          leftIcon={leftIcon}
          rightIcon={{
            name: IconName.CHEVRON_RIGHT,
          }}
          label={label}
          onClick={this.onMouseEnter}
          isFocused={focused || this.state.isOpen}
          testId={testId}
          intercomTarget={intercomTarget}
          popoverProps={popoverProps}
          tooltipText={tooltipText}
          description={description}
          leftContent={leftContent}
        />
        {this.state.isOpen && (
          <ModalBox
            getHostBox={this.getHostBox}
            getPositionAndMaxHeight={(refBox, modalBox) =>
              getPositionAndMaxHeightHorizontally(refBox, modalBox, -4)
            }
            isScrollable={isScrollable}
            maxDropdownSize={maxDropdownSize}
            minDropdownSize={minDropdownSize}
            isScrollLockDisabled
          >
            {renderSubmenuList({
              options,
              addKeyboardListener,
              removeKeyboardListener,
              closeMenu: this.closeMenu,
            })}
          </ModalBox>
        )}
      </div>
    );
  }

  openSubmenu(event: React.MouseEvent<HTMLElement>) {
    this.closeMenuDebounced.cancel();
    event.stopPropagation();
    event.preventDefault();
    this.setState({ isOpen: true });
  }
}

export { ModalSubmenu };
